var dayjs = require('../../utils/dayjsConfig');

// pressure_samples are stored at 1/10 the scale of the per-event pressure value
// carried on each WFR sample (verified against live data: e-piezo pressure_sample
// 791.74 ≈ WFR pressure 7896.78 / 10). Reconstructing synthetic pressure_samples
// from the WFR stream lets pressure-only sensors (high_pressure / low_pressure) —
// which return an empty pressure_samples stream while still reporting pressure on
// each WFR frame — render the same pressure graph as e-piezos.
const WFR_PRESSURE_SCALE = 10;

// Offset (seconds) from each frame edge for the held-constant pressure points,
// kept tiny so the plateau spans nearly the whole event with near-vertical sides
// while staying strictly inside (ts, end_date) so the framing logic keeps them.
const FRAME_EDGE_OFFSET_SECONDS = 1;

// Number of evenly-spaced, equal-value points emitted across each event. The graph
// builder's density reducer (`reduceSampleDensity(_, false)`) drops ~half of the
// non-zero points once the whole series exceeds 50 points, so a mere 2 points per
// event collapse back to a single spike (triangle). Sampling the constant pressure
// at several points keeps survivors near both edges -> the plateau stays flat.
const PLATEAU_SAMPLE_COUNT = 8;

// Build pressure_samples from recent_wfr_samples for sensors with no dedicated
// pressure stream. The per-event pressure is held CONSTANT across the whole event
// duration so the graph draws a flat plateau ("hill") per event rather than a
// triangle — matching the piezometer look. Frames too short for distinct interior
// points fall back to a single midpoint point.
const buildPressureSamplesFromWfr = (wfrSamples) => {
  if (!Array.isArray(wfrSamples)) {
    return [];
  }
  return wfrSamples.reduce((acc, sample) => {
    const pressure = Number.parseFloat(sample?.pressure);
    const start = parseInt(sample?.ts);
    const end = parseInt(sample?.end_date);
    if (isNaN(pressure) || isNaN(start) || isNaN(end) || end <= start) {
      return acc;
    }
    const value = pressure / WFR_PRESSURE_SCALE;
    const innerStart = start + FRAME_EDGE_OFFSET_SECONDS;
    const innerEnd = end - FRAME_EDGE_OFFSET_SECONDS;
    if (innerEnd <= innerStart) {
      const midpointMs = Math.floor((start + end) / 2) * 1000;
      acc.push({ date: dayjs(midpointMs).format('L LTS Z'), value });
      return acc;
    }
    const span = innerEnd - innerStart;
    const count = Math.min(PLATEAU_SAMPLE_COUNT, span + 1);
    for (let k = 0; k < count; k++) {
      const second = innerStart + Math.round((k * span) / (count - 1));
      acc.push({ date: dayjs(second * 1000).format('L LTS Z'), value });
    }
    return acc;
  }, []);
};

// Decide the pressure_samples used by the graph. A populated pressure_samples
// stream (an object keyed by timestamp) is used as-is; when it is empty
// (pressure-only sensors) we fall back to the per-event pressure carried on the
// WFR samples so the graph still renders real data instead of a flat zero line.
const derivePressureSamples = (body) => {
  const pressureEntries = Object.entries(body?.pressure_samples || {});
  if (pressureEntries.length > 0) {
    return pressureEntries.map((entry) => ({
      date: dayjs(parseInt(entry[0]) * 1000).format('L LTS Z'),
      value: entry[1],
    }));
  }
  return buildPressureSamplesFromWfr(body?.recent_wfr_samples);
};

module.exports = {
  WFR_PRESSURE_SCALE,
  buildPressureSamplesFromWfr,
  derivePressureSamples,
};
